import { useState, useEffect } from "react"
import { Trash2 } from "lucide-react"
import { useNavigate } from "react-router-dom"
import { useAppDispatch, useAppSelector } from "../app/hooks"
import { fetchCompanyMembers } from "../features/companyMembers/companyMemberSlice"
import { deleteCompanyMember } from "../features/companyMembers/companyMemberAPI"
import { toast } from "sonner"
import { ProfileImage } from "../components/ProfileImage"

const ITEMS_PER_PAGE = 7

export default function AssignRoles() {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { members: teamMembers, status, error } = useAppSelector(
    (state) => state.companyMembers
  )
  const accessToken = useAppSelector((state) => state.auth.accessToken)
  const [currentPage, setCurrentPage] = useState(1)
  const [deletingMemberId, setDeletingMemberId] = useState<number | null>(null)

  useEffect(() => {
    dispatch(fetchCompanyMembers())
  }, [dispatch])

  const handleDelete = async (memberId: number, memberName: string) => {
    // Show confirmation dialog
    const confirmed = window.confirm(
      `Are you sure you want to delete ${memberName}? This action cannot be undone.`
    )

    if (!confirmed) {
      return
    }

    try {
      setDeletingMemberId(memberId)
      const result = await deleteCompanyMember(memberId)

      if (result.error) {
        toast.error(result.error)
      } else {
        toast.success("Member deleted successfully")
        // Refresh the member list
        dispatch(fetchCompanyMembers())
      }
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to delete member. Please try again."
      toast.error(message)
    } finally {
      setDeletingMemberId(null)
    }
  }

  const loading = status === "loading"
  const totalPages = Math.ceil(teamMembers.length / ITEMS_PER_PAGE)
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE
  const displayedMembers = teamMembers.slice(startIndex, startIndex + ITEMS_PER_PAGE)

  // Reset to page 1 when data changes
  useEffect(() => {
    setCurrentPage(1)
  }, [teamMembers.length])

  return (
    <div className="min-h-screen bg-background p-8">
      {/* Header with Add Button */}
      <div className="flex items-end justify-end mb-8">
        <button
          onClick={() => navigate("/create-member")}
          className="flex bg-black text-white items-center gap-2 bg-foreground text-background px-4 py-2 rounded-md hover:bg-foreground/90 transition-colors"
        >
          <span className="text-lg">+</span>
          <span>Add User</span>
        </button>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <div className="text-foreground">Loading...</div>
        </div>
      )}

      {/* Error State */}
      {error && !loading && (
        <div className="bg-destructive/10 border border-destructive rounded-md p-4 mb-6">
          <p className="text-destructive">{error}</p>
        </div>
      )}

      {/* Table Container */}
      {!loading && !error && (
        <>
          <div className="overflow-hidden rounded-md">
            <div className="overflow-x-auto">
              <table className="w-full table-fixed border-collapse">
                <colgroup>
                  <col className="w-[32%]" />
                  <col className="w-[18%]" />
                  <col className="w-[22%]" />
                  <col className="w-[18%]" />
                  <col className="w-[10%]" />
                </colgroup>
                {/* Table Header */}
                <thead>
                  <tr>
                    <th className="px-6 py-4 font-bold text-left text-sm text-black bg-[#CDD6DF] rounded-l-md">Name</th>
                    <th className="px-6 py-4 font-bold text-center text-sm text-black bg-[#CDD6DF]">Role</th>
                    <th className="px-6 py-4 font-bold text-center text-sm text-black bg-[#CDD6DF]">Designation</th>
                    <th className="px-6 py-4 font-bold text-center text-sm text-black bg-[#CDD6DF]">Department</th>
                    <th className="px-6 py-4 font-bold text-center text-sm text-black bg-[#CDD6DF] rounded-r-md">Actions</th>
                  </tr>
                </thead>

                {/* Table Body */}
                <tbody>
                  {displayedMembers.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-6 py-8 text-center text-muted-foreground">
                        No team members found.
                      </td>
                    </tr>
                  ) : (
                    displayedMembers.map((member, index) => (
                      <tr
                        key={member.id}
                        className={`transition-colors hover:bg-gray-100`}
                      >
                        {/* Name with Avatar */}
                        <td className="px-6 py-4">
                          <div className="flex min-w-0 items-center gap-2">
                            <ProfileImage
                              profilePicture={member.avatar}
                              accessToken={accessToken || undefined}
                              alt={member.name}
                              className="min-h-10 min-w-10 max-h-10 max-w-10 rounded-md bg-gray-200 border-2 border-black"
                              fallbackPath="/assets/images/Avatar2.jpg"
                            />
                            <span
                              onClick={() => navigate(`/create-member?edit=${member.id}`)}
                              className="block flex-1 truncate font-medium text-foreground cursor-pointer"
                              title={member.name}
                            >
                              {member.name}
                            </span>
                          </div>
                        </td>

                        {/* Role */}
                        <td className="px-6 py-4 text-center text-sm font-medium text-[#192A47]">
                          <span className="block truncate" title={member.role}>{member.role}</span>
                        </td>

                        {/* Designation */}
                        <td className="px-6 py-4 text-center text-sm font-medium text-[#192A47]">
                          <span className="block truncate" title={member.designation}>{member.designation}</span>
                        </td>

                        {/* Department */}
                        <td className="px-6 py-4 text-center text-sm font-medium text-[#192A47]">
                          <span className="block truncate" title={member.department}>{member.department}</span>
                        </td>

                        {/* Actions */}
                        <td className="px-6 py-4">
                          <div className="flex items-center justify-center gap-1">
                            <button
                              onClick={() => navigate(`/create-member?edit=${member.id}`)}
                              className="text-muted-foreground hover:text-foreground transition-colors p-1.5 hover:bg-secondary rounded-md"
                            >
                              <svg width="22" height="22" viewBox="0 0 24 27" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path fill-rule="evenodd" clip-rule="evenodd" d="M13.8787 4.17052C15.0503 2.8525 16.9497 2.8525 18.1213 4.17052L20.2929 6.61354C21.4645 7.93156 21.4645 10.0685 20.2929 11.3865L11.4142 21.375H21C21.5523 21.375 22 21.8787 22 22.5C22 23.1213 21.5523 23.625 21 23.625H4C3.44772 23.625 3 23.1213 3 22.5V16.875C3 16.5767 3.10536 16.2905 3.29289 16.0795L13.8787 4.17052ZM8.58579 21.375L18.8787 9.79552C19.2692 9.35618 19.2692 8.64387 18.8787 8.20453L16.7071 5.76151C16.3166 5.32217 15.6834 5.32217 15.2929 5.76151L5 17.341V21.375H8.58579Z" fill="black" />
                              </svg>

                            </button>
                            <button
                              onClick={() => handleDelete(member.id, member.name)}
                              disabled={deletingMemberId === member.id}
                              className="text-muted-foreground hover:text-destructive transition-colors p-1.5 hover:bg-destructive/10 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
                              title="Delete member"
                            >
                              <Trash2 size={18} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          {totalPages > 0 && (
            <div className="mt-6 flex items-center justify-end gap-2">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="px-4 py-2 text-sm font-medium text-[#9E9E9E] transition-colors hover:text-gray-900 disabled:cursor-not-allowed disabled:opacity-60"
              >
                Previous
              </button>

              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                <button
                  key={page}
                  onClick={() => setCurrentPage(page)}
                  className={`flex h-9 w-9 items-center justify-center rounded-md text-sm font-bold transition-colors ${currentPage === page
                    ? "bg-black text-white"
                    : "bg-white text-gray-600 hover:bg-gray-100"
                    }`}
                >
                  {page}
                </button>
              ))}

              <button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="px-4 py-2 text-sm font-medium text-[#9E9E9E] transition-colors hover:text-gray-900 disabled:cursor-not-allowed disabled:opacity-60"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  )
}
